import { useMemo } from "react";

import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { MunicipalPropertyContributionFilter, MunicipalPropertyRegisterContentQuery } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { DEFAULT_STALE_TIME_MS } from "@/services/react-query.service/stale-time";
import { MUNICIPAL_PROPERTY_LIST_PAGE_SIZE } from "@ark/shared";
import { InfiniteData, QueryFunctionContext, useInfiniteQuery, UseInfiniteQueryResult } from "@tanstack/react-query";

import { MUNICIPAL_PROPERTY_YEAR_LIST_QUERY_DOCUMENT } from "./municipal-property-year-list-query.gql";

type RegisterContent = {
  municipalPropertyYearList: MunicipalPropertyRegisterContentQuery["municipalPropertyYearList"]["nodeList"];
};
type RegisterContentQueryResult = UseInfiniteQueryResult<
  InfiniteData<MunicipalPropertyRegisterContentQuery, string | undefined>,
  Error
>;
type RegisterContentResult = Omit<RegisterContentQueryResult, "data"> & { data: RegisterContent | undefined };

type RegisterContentScope = { registerId: string } & (
  | { inventoryId?: string; contribution?: never }
  | { inventoryId: string; contribution: MunicipalPropertyContributionFilter }
);

export function useMunicipalPropertyRegisterContent({ registerId, contribution, inventoryId }: RegisterContentScope): RegisterContentResult {
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const queryResult: RegisterContentQueryResult = useInfiniteQuery({
    queryKey: contribution ? queryKeys.municipalPropertyYears.contributions({ registerId, contribution, inventoryId }) : queryKeys.municipalPropertyYears.list({ registerId, inventoryId }),
    queryFn: ({
      pageParam,
    }: QueryFunctionContext<
      ReturnType<typeof queryKeys.municipalPropertyYears.list> | ReturnType<typeof queryKeys.municipalPropertyYears.contributions>,
      string | undefined
    >): Promise<MunicipalPropertyRegisterContentQuery> =>
      createOperationClient("MunicipalPropertyRegisterContent").request(MUNICIPAL_PROPERTY_YEAR_LIST_QUERY_DOCUMENT, {
        registerId,
        contribution,
        inventoryId,
        first: MUNICIPAL_PROPERTY_LIST_PAGE_SIZE.default,
        after: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: MunicipalPropertyRegisterContentQuery): string | undefined =>
      lastPage.municipalPropertyYearList.hasNextPage
        ? (lastPage.municipalPropertyYearList.endCursor ?? undefined)
        : undefined,
    enabled: !!registerId,
    staleTime: DEFAULT_STALE_TIME_MS,
    meta: { workspaceId: currentWorkspace.id },
  });

  const data: RegisterContent | undefined = useMemo(
    function flattenMembershipPages(): RegisterContent | undefined {
      const pageList: MunicipalPropertyRegisterContentQuery[] | undefined = queryResult.data?.pages;
      if (!pageList) return undefined;
      return {
        municipalPropertyYearList: pageList.flatMap(
          (page: MunicipalPropertyRegisterContentQuery): RegisterContent["municipalPropertyYearList"] =>
            page.municipalPropertyYearList.nodeList,
        ),
      };
    },
    [queryResult.data?.pages],
  );

  return { ...queryResult, data };
}
